import { useEffect, useState } from "react";
import {
  Tr,
  Th,
  Td,
  Box,
  Flex,
  Heading,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Spinner,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  IconButton,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { FaCheck } from "react-icons/fa";
import { getProcessById } from "../services/processService";
import { ProcessViewModel } from "../models/Process";
import { DateTime } from "luxon";
import { crm } from "../theme";

interface EditableCell {
  rowId: string | number;
  columnLabel: string;
  nestedItemId?: string | number;
}

const NestedTableRow = ({
  row,
  columns,
  nestedColumns,
  isMobile,
  financial,
  isProcess = false,
  onCellEdit,
  onNestedCellEdit,
}: {
  isProcess?: boolean;
  row: any;
  isMobile: boolean;
  financial: boolean;
  columns: {
    label: string;
    data: any;
    fixed?: boolean;
    width?: string[] | string | number;
    editable?: boolean;
    index?: string; // Maps to the actual field name in the data
    type?: "text" | "number" | "date" | "select" | "currency";
    options?: { label: string; value: string }[];
  }[];
  nestedColumns?: {
    label: string;
    data: any;
    width?: string[] | string | number;
    editable?: boolean;
    index?: string;
    type?: "text" | "number" | "date" | "select" | "currency";
    options?: { label: string; value: string }[];
  }[];
  onCellEdit?: (rowId: any, index: string, newValue: any) => Promise<void>;
  onNestedCellEdit?: (
    rowId: any,
    itemId: any,
    index: string,
    newValue: any,
  ) => Promise<void>;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState<ProcessViewModel>(row);
  const [editingCell, setEditingCell] = useState<EditableCell | null>(null);
  const [editValue, setEditValue] = useState<any>("");
  const [clickCount, setClickCount] = useState(0);
  const [clickTimer, setClickTimer] = useState<NodeJS.Timeout | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const toast = useToast();

  const toggleCellExpanded = (label: string) => {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Non-editable cells: own onClick toggles expand, no other handler on the cell to conflict with.
  const renderTruncatable = (content: any, columnLabel: string) => {
    if (typeof content !== "string" || content.length <= 30) return content;
    const isExpanded = expandedCells.has(columnLabel);
    return (
      <Box
        maxW={isExpanded ? undefined : "220px"}
        whiteSpace={isExpanded ? "normal" : "nowrap"}
        overflow={isExpanded ? "visible" : "hidden"}
        textOverflow={isExpanded ? undefined : "ellipsis"}
        wordBreak={isExpanded ? "break-word" : undefined}
        title={content}
        cursor="pointer"
        onClick={(e) => {
          e.stopPropagation();
          toggleCellExpanded(columnLabel);
        }}
      >
        {content}
      </Box>
    );
  };

  // Editable cells: no own onClick — the wrapping cell's handleCellClick (single/double click) owns expand vs edit.
  const renderTruncatableStatic = (content: any, columnLabel: string) => {
    if (typeof content !== "string" || content.length <= 30) return content;
    const isExpanded = expandedCells.has(columnLabel);
    return (
      <Box
        maxW={isExpanded ? undefined : "220px"}
        whiteSpace={isExpanded ? "normal" : "nowrap"}
        overflow={isExpanded ? "visible" : "hidden"}
        textOverflow={isExpanded ? undefined : "ellipsis"}
        wordBreak={isExpanded ? "break-word" : undefined}
        title={content}
        display="inline-block"
        verticalAlign="middle"
      >
        {content}
      </Box>
    );
  };

  const getDetails = async () => {
    const response = await getProcessById(row.id);
    if (response) {
      setData(response);
    }
  };

  useEffect(() => {
    if (!financial && expanded) {
      getDetails();
    }
  }, [expanded]);

  const handleCellClick = (
    rowId: any,
    index: string,
    currentValue: any,
    isEditable: boolean,
    nestedItemId?: any,
    truncateKey?: string,
  ) => {
    if (!isEditable) return;

    setClickCount((prev) => prev + 1);

    if (clickTimer) {
      clearTimeout(clickTimer);
    }

    const timer = setTimeout(() => {
      if (clickCount + 1 === 2) {
        // Double click detected
        setEditingCell({ rowId, columnLabel: index, nestedItemId });

        // Format value based on type for editing
        let formattedValue = currentValue;
        if (currentValue instanceof Date) {
          formattedValue =
            DateTime.fromJSDate(currentValue).toFormat("yyyy-LL-dd");
        } else if (
          typeof currentValue === "string" &&
          currentValue.includes("$")
        ) {
          // Remove currency prefix for editing
          formattedValue = currentValue.replace(/^[A-Z]{3}\s/, "");
        }

        setEditValue(formattedValue);
      } else if (clickCount + 1 === 1 && truncateKey) {
        toggleCellExpanded(truncateKey);
      }
      setClickCount(0);
    }, 300);

    setClickTimer(timer);
  };

  const handleSaveEdit = async (
    rowId: any,
    index: string,
    column: any,
    nestedItemId?: any,
  ) => {
    setIsSaving(true);
    try {
      let valueToSave = editValue;

      // Format value based on type before saving
      if (column.type === "currency" && editValue) {
        // Add currency prefix back
        const currencyPrefix = data.totalInvoice?.split(" ")[0] || "USD";
        valueToSave = `${currencyPrefix} ${editValue}`;
      } else if (column.type === "date" && editValue) {
        // Ensure date is in ISO format
        valueToSave = DateTime.fromFormat(editValue, "yyyy-LL-dd").toISO();
      }

      if (isProcess && typeof valueToSave === "string") {
        valueToSave = valueToSave.toUpperCase();
      }

      if (nestedItemId !== undefined && onNestedCellEdit) {
        // Save nested item edit
        await onNestedCellEdit(rowId, nestedItemId, index, valueToSave);

        // Update local nested data
        setData((prev) => ({
          ...prev,
          items: prev.items?.map((item: any) =>
            item.id === nestedItemId ? { ...item, [index]: valueToSave } : item,
          ),
        }));
      } else if (onCellEdit) {
        // Save main row edit
        await onCellEdit(rowId, index, valueToSave);

        // Update local data
        setData((prev) => ({
          ...prev,
          [index]: valueToSave,
        }));
      }

      toast({
        title: "Campo atualizado",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error saving edit:", error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar a alteração",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
      setEditingCell(null);
      setEditValue("");
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    rowId: any,
    index: string,
    column: any,
    nestedItemId?: any,
  ) => {
    // Só o check salva; Enter não grava (evita salvar sem querer), Esc descarta
    if (e.key === "Enter") {
      e.preventDefault();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const renderInput = (
    column: any,
    rowId: any,
    index: string,
    nestedItemId?: any,
  ) => {
    // Só salva no check (ou Enter); clicar fora descarta e volta ao valor anterior.
    const commonProps = {
      value: editValue,
      onBlur: handleCancelEdit,
      onKeyDown: (e: React.KeyboardEvent) =>
        handleKeyDown(e, rowId, index, column, nestedItemId),
      size: "sm",
      autoFocus: true,
      isDisabled: isSaving,
    };

    switch (column.type) {
      case "number":
      case "currency":
        return (
          <NumberInput
            {...commonProps}
            onChange={(valueString) => setEditValue(valueString)}
          >
            <NumberInputField />
          </NumberInput>
        );

      case "date":
        return (
          <Input
            {...commonProps}
            type="date"
            onChange={(e) => setEditValue(e.target.value)}
          />
        );

      case "select":
        return (
          <Select
            {...commonProps}
            onChange={(e) => {
              setEditValue(e.target.value);
            }}
          >
            {column.options?.map((opt: any) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        );

      default:
        return (
          <Input
            {...commonProps}
            onChange={(e) => setEditValue(e.target.value)}
          />
        );
    }
  };

  const renderEditableCell = (
    displayValue: any,
    actualValue: any,
    rowId: any,
    column: any,
    nestedItemId?: any,
  ) => {
    const isEditing =
      editingCell?.rowId === rowId &&
      editingCell?.columnLabel === column.index &&
      editingCell?.nestedItemId === nestedItemId;

    const isEditable = column.editable === true;

    if (isEditing) {
      return (
        <Flex align="center" gap="4px" minW="160px">
          <Box flex="1" minW={0}>
            {renderInput(column, rowId, column.index!, nestedItemId)}
          </Box>
          <Tooltip label="Salvar" openDelay={400}>
            <IconButton
              aria-label="Salvar alteração"
              icon={<FaCheck />}
              size="sm"
              colorScheme="green"
              isLoading={isSaving}
              // mousedown sem preventDefault tiraria o foco do input e o blur descartaria a edição
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSaveEdit(rowId, column.index!, column, nestedItemId)}
            />
          </Tooltip>
        </Flex>
      );
    }

    return (
      <Box
        onClick={() =>
          handleCellClick(
            rowId,
            column.index!,
            actualValue,
            isEditable,
            nestedItemId,
            isProcess ? column.label : undefined,
          )
        }
        cursor={isEditable ? "pointer" : "default"}
        _hover={
          isEditable
            ? {
                backgroundColor: "gray.50",
                outline: "1px solid",
                outlineColor: "blue.200",
              }
            : {}
        }
        padding="4px 8px"
        borderRadius="4px"
        transition="all 0.2s"
        position="relative"
      >
        {isSaving && isEditable ? (
          <Spinner size="sm" />
        ) : isProcess ? (
          renderTruncatableStatic(displayValue, column.label)
        ) : (
          displayValue
        )}
      </Box>
    );
  };

  return (
    <>
      <Tr
        key={data.id}
        bg={data.isUrgent ? "rgba(220,38,38,0.06)" : "white"}
        borderLeft={data.isUrgent ? "4px solid" : undefined}
        borderLeftColor={data.isUrgent ? crm.danger : undefined}
      >
        {columns.map((column, index) => {
          const cellContent = column.data(data, expanded, setExpanded);
          const isEditable = column.editable === true;
          const isActionColumn = column.label.length === 0;
          const isFixedColumn = column.fixed === true || (isProcess && index === 0);
          const stickyBackground = data.isUrgent ? "#FFF1F2" : "white";
          const actualValue = column.index
            ? data[column.index as keyof typeof data]
            : cellContent;

          return (
            <Td
              borderLeftWidth={isProcess && index === 0 ? "3px" : 0}
              borderLeftColor={
                isProcess && index === 0 && data.status === "ENCERRADO"
                  ? "lightgray"
                  : isProcess && index === 0
                    ? "var(--icon-color)"
                    : undefined
              }
              zIndex={isActionColumn ? 3 : isFixedColumn ? 2 : undefined}
              backgroundColor={
                isActionColumn || isFixedColumn ? stickyBackground : undefined
              }
              position={
                isActionColumn || isFixedColumn ? "sticky" : undefined
              }
              right={isActionColumn ? 0 : undefined}
              left={isFixedColumn ? 0 : undefined}
              bg={
                isActionColumn || isFixedColumn
                  ? stickyBackground
                  : data.isUrgent
                    ? "rgba(220,38,38,0.06)"
                    : "white"
              }
              style={
                isActionColumn || isFixedColumn
                  ? { backgroundColor: stickyBackground }
                  : undefined
              }
              textTransform="none"
              key={column.label}
              padding={isEditable ? "4px" : undefined}
              minW={isActionColumn ? "176px" : column.width}
              whiteSpace={isActionColumn ? "nowrap" : undefined}
            >
              {isEditable && column.index
                ? renderEditableCell(cellContent, actualValue, data.id, column)
                : isProcess
                  ? renderTruncatable(cellContent, column.label)
                  : cellContent}
            </Td>
          );
        })}
      </Tr>
      {nestedColumns && expanded && (
        <Tr>
          {data.items?.length > 0 ? (
            <Td colSpan={columns.length}>
              <Box backgroundColor="whitesmoke">
                {isMobile ? (
                  <Flex
                    backgroundColor="whitesmoke"
                    flexDirection="column"
                    padding="16px"
                    gap="8px"
                  >
                    {nestedColumns.map((column) => (
                      <Box key={column.label}>
                        {column.data(data).props?.children && (
                          <Box>
                            <Heading size="lg" width={column.width}>
                              {column.label.length > 0
                                ? `${column.label}:`
                                : ""}
                            </Heading>
                            {column.data(data)}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Flex>
                ) : (
                  <TableContainer w="87vw" overflowX="scroll">
                    <Table>
                      <Thead>
                        <Tr>
                          {nestedColumns.map((column) => (
                            <Th
                              backgroundColor="whitesmoke"
                              position={
                                column.label.length === 0 ? "sticky" : undefined
                              }
                              right={column.label.length === 0 ? 0 : undefined}
                              key={column.label}
                              width={column.width}
                            >
                              {column.label}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.items.map((item: any) => (
                          <Tr key={item.id}>
                            {nestedColumns.map((column) => {
                              const cellContent = column.data(item);
                              const isEditable = column.editable === true;
                              const actualValue = column.index
                                ? item[column.index]
                                : cellContent;

                              return (
                                <Td
                                  backgroundColor="whitesmoke"
                                  zIndex={1000}
                                  position={
                                    column.label.length === 0
                                      ? "sticky"
                                      : undefined
                                  }
                                  right={
                                    column.label.length === 0 ? 0 : undefined
                                  }
                                  key={column.label}
                                  padding={isEditable ? "4px" : undefined}
                                >
                                  {isEditable && column.index
                                    ? renderEditableCell(
                                        cellContent,
                                        actualValue,
                                        data.id,
                                        column,
                                        item.id,
                                      )
                                    : cellContent}
                                </Td>
                              );
                            })}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Td>
          ) : (
            <Spinner />
          )}
        </Tr>
      )}
    </>
  );
};

export default NestedTableRow;
